import {
  getDoc,
  updateDoc,
} from 'firebase/firestore'
import i18n from '@/libs/i18n'
import store from '@/store'
import { getAuth0Instance as authInstance } from '@/plugins/auth0'
import { getTenantContextInstance as tenantCtx } from '@/plugins/tenant'

export default {
  namespaced: true,
  state: {
    featureFlags: {},
  },
  getters: {
    getFeatureFlags: state => state.featureFlags,
  },
  mutations: {
    SET_FEATURE_FLAGS(state, payload) {
      state.featureFlags = payload
    },
  },
  actions: {
    /**
     * Fetches the tenant's feature flags.
     *
     * @param {Object} commit
     *
     * @returns {Promise} Retrieves all fields in the document as an Object.
     */
    async fetchFeatureFlags({ commit }) {
      const { tenant } = tenantCtx()

      return new Promise((resolve, reject) => {
        getDoc(tenant)
          .then(docSnapshot => {
            if (!docSnapshot.exists()) {
              resolve()

              return
            }

            const data = docSnapshot.data()

            if (!data.featureFlags) {
              commit('SET_FEATURE_FLAGS', {})
              resolve({})

              return
            }

            commit('SET_FEATURE_FLAGS', data.featureFlags)

            resolve(data.featureFlags)
          })
          .catch(error => {
            console.debug(error)

            reject(error)
          })
      })
    },

    /**
     * Updates the tenant's feature flags document field.
     * Requires the `update:feature_flags` permission.
     *
     * @param {Object} context
     * @param {Object} featureFlags Partial or full featureFlags map to merge
     *
     * @returns {Promise<Object>} The merged feature flags
     */
    async updateFeatureFlags({ commit, state }, featureFlags) {
      if (!authInstance().user?.ability?.can('update', 'feature_flags')) {
        store.dispatch('notify', {
          body: i18n.t('You do not have sufficient privileges to access this resource.'),
          variant: 'danger',
        })

        return Promise.reject(new Error('Unauthorized'))
      }

      const { tenant } = tenantCtx()
      const next = {
        ...state.featureFlags,
        ...featureFlags,
      }

      return new Promise((resolve, reject) => {
        updateDoc(tenant, { featureFlags: next })
          .then(() => {
            commit('SET_FEATURE_FLAGS', next)
            store.dispatch('notify', {
              body: i18n.t('Feature flags have been updated'),
            })
            resolve(next)
          })
          .catch(error => {
            console.debug(error)
            store.dispatch('notify', {
              body: i18n.t('Something went wrong updating the feature flags'),
              variant: 'danger',
            })
            reject(error)
          })
      })
    },
  },
}
